import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Beaker, Target } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, toIsoDate } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { Paged } from './pagedList';

const LEVELS = ['low', 'normal', 'high'];
const DEFAULT_SHELF_LIFE_MS = 180 * 86_400_000;

export function QcLotPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/lab/qc');
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    testId: params.get('testId') ?? '',
    level: 'normal',
    lotNo: '',
    manufacturer: '',
    expiryDate: toIsoDate(new Date(Date.now() + DEFAULT_SHELF_LIFE_MS)),
    targetMean: '',
    targetSd: '',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const tests = useQuery({
    queryKey: ['lab-tests'],
    queryFn: async () => (await get<Paged<{ id: string; code: string; name: string; unit: string | null }>>('/lab/tests?limit=300')).data,
  });

  const save = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/lab/qc/lots', {
        testId: form.testId,
        level: form.level,
        lotNo: form.lotNo,
        manufacturer: form.manufacturer || undefined,
        expiryDate: form.expiryDate,
        targetMean: Number(form.targetMean),
        targetSd: Number(form.targetSd),
      }),
    onSuccess: (lot) => {
      toast.success(tr('lab', 'control_lot_added'));
      void queryClient.invalidateQueries({ queryKey: ['qc-lots'] });
      const separator = returnTo.includes('?') ? '&' : '?';
      navigate(`${returnTo}${separator}lotId=${lot.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.testId) && Boolean(form.lotNo) && Boolean(form.targetMean) && Boolean(form.targetSd);
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={tr('lab', 'add_a_control_lot')}
      description={tr('lab', 'the_target_mean_and_sd_come_from_the')}
      eyebrow={tr('lab', 'quality_control')}
      icon={Beaker}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('lab', 'lot')} icon={Beaker}>
        <Field label={tr('lab', 'test')} required className="sm:col-span-2">
          <Select value={form.testId} onChange={(e) => setForm({ ...form, testId: e.target.value })}>
            <option value="">{tr('lab', 'select')}</option>
            {tests.data?.map((test) => (
              <option key={test.id} value={test.id}>
                {test.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('lab', 'level')}>
          <ChoiceButtons<string>
            value={form.level}
            onChange={(value) => setForm({ ...form, level: value })}
            options={LEVELS.map((l) => ({ value: l, label: humanize(l) }))}
          />
        </Field>
        <Field label={tr('lab', 'lot_number')} required>
          <Input value={form.lotNo} onChange={(e) => setForm({ ...form, lotNo: e.target.value })} />
        </Field>
        <Field label={tr('lab', 'manufacturer')}>
          <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
        </Field>
        <Field label={tr('lab', 'expires')} required>
          <DatePicker value={form.expiryDate} onChange={(value) => setForm({ ...form, expiryDate: value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('lab', 'target_mean')} icon={Target}>
        <Field label={tr('lab', 'target_mean')} required>
          <Input type="number" step="any" value={form.targetMean} onChange={(e) => setForm({ ...form, targetMean: e.target.value })} />
        </Field>
        <Field label={tr('lab', 'target_sd')} required hint={tr('lab', 'must_be_greater_than_zero')}>
          <Input type="number" step="any" min={0} value={form.targetSd} onChange={(e) => setForm({ ...form, targetSd: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('lab', 'add')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
